// V-A-1 — Scheduling Mutation Contract.
//
// Canonical client-side entry point for ticket scheduling-field mutations.
//
// AUTHORITY CONTRACT:
// - Allowlist below is the ONLY set of fields callers may change through this
//   helper. Anything else is rejected so unrelated columns can't piggy-back on
//   a "scheduling" code path and silently drift the schedule contract.
// - Helper resolves shop_id / scheduled_date / tech for cascade scoping and
//   fires the canonical board cascade post-update unless `skipCascade`.
// - Helper preserves existing behavior — it does NOT replace recompute,
//   rollover-chain CAS propagation, break-row sync, or the timer state machine.
//   Those callers still own their writes; they will be migrated in V-A-2/V-A-3.
//
// SERVER-SIDE NOTE:
// - Edge functions (cascade-board, smart-assign, reassign-ticket,
//   eod-rollover, etc.) own their own writes via service-role and are NOT
//   covered here. A server-side mirror with the same allowlist is tracked
//   as V-A-2/V-A-3 work.
import { supabase } from '@/integrations/supabase/client';
import type { SupabaseClient } from '@supabase/supabase-js';
import { triggerBoardCascade } from '@/utils/cascadeTrigger';
import { getShopDayLabel } from '@/utils/shopTime';
// DTC-G G2: route this canonical schedule-mutation helper's own audit
// timestamps (`last_edited_at`) through the canonical clock so the
// mutator that locks every other DTC-F V10-class write is itself
// deterministic under `clock.setFixed(...)`.
import { clock } from '@/utils/clock';
// V-A-3: single source of truth for the scheduling-mutation allowlist —
// shared zero-dep module also imported by the server helper at
// supabase/functions/_shared/mutateTicketSchedule.ts. Adding a field here
// would silently desync the contract; edit scheduleAllowlist.ts instead.
import {
  ALLOWED_SCHEDULE_FIELDS,
  type ScheduleField,
  type ScheduleUpdates,
} from '../../supabase/functions/_shared/scheduleAllowlist';
// Phase 5.2 — Due Out companion-field gate. Pure helper; fails closed
// when `companionFields.due_out` differs from the caller-supplied
// `oldDueOut` without a valid audit context.
import {
  gateDueOutCompanionField,
  type DueOutEditAuditContext,
  type DueOutEditAuditPayload,
} from './dueOutEditAuthorization';

export { ALLOWED_SCHEDULE_FIELDS };
export type { ScheduleField, ScheduleUpdates };
export type { DueOutEditAuditContext, DueOutEditAuditPayload };

export interface MutateTicketScheduleOptions {
  // Required. Surfaces in error/warn logs so every schedule mutation is
  // attributable when something cascades wrong.
  reason: string;

  // Skip the post-update board cascade. Set true for callers that batch many
  // mutations and trigger a single cascade themselves (e.g. sequenceNormalizer).
  // When true, the helper also skips the context fetch — caller takes full
  // responsibility for cascade scoping.
  skipCascade?: boolean;

  // Optional acting user — written to last_edited_by_user_id alongside the
  // update so we get a paper trail without a follow-up write.
  userId?: string;

  // V-A-5: opt-in non-schedule columns written ATOMICALLY with the schedule
  // update. Mirrors the server helper's option (V-A-2). Use for caller-owned
  // metadata that must land in the same row write — `type`, `is_comeback`,
  // `due_out`, `pause_reason`, `paused_at`, `picked_up_at`, etc. Splitting
  // these into a follow-up `.update()` would lose atomicity.
  //
  // Companion fields BYPASS the schedule allowlist by design. Callers MUST
  // keep this list small and own validation for whatever they put here. The
  // helper fail-closes if a companion key collides with the schedule
  // allowlist (use `updates` for those instead).
  companionFields?: Record<string, unknown>;

  // Override scheduled_date used to scope the cascade. Default: prefer
  // `updates.scheduled_date`, then the ticket's current `scheduled_date`,
  // then derive from start_time in shop tz.
  cascadeDateOverride?: string;

  // Override tech id for cascade scope. Pass `null` for explicit all-tech
  // cascade. Default: prefer `updates.assigned_tech_id`, then ticket's
  // current `assigned_tech_id`.
  cascadeTechOverride?: string | null;

  // Phase 5.2 — Required when `companionFields.due_out` differs from
  // the caller-supplied `oldDueOut`. The helper passes this context to
  // `gateDueOutCompanionField` (pure) and fails closed if invalid.
  //
  // Doctrine: Due Out is a hard customer-promise constraint. Bucket 4.5
  // §4.5.7 — every Due Out edit must be authorized, reasoned, and
  // audit-logged. Phase 5.1 supplies the helper; Phase 5.2 enforces the
  // gate at this mutation choke point.
  //
  // Omit this option entirely when the call does not change `due_out`.
  // The helper does NOT require it for unchanged-due-out paths or for
  // mutations that don't touch the companion `due_out` at all.
  dueOutEditAuditContext?: DueOutEditAuditContext;
}

export interface MutateTicketScheduleResult {
  success: boolean;
  data?: {
    ticketId: string;
    appliedFields: ScheduleField[];
    appliedCompanionFields: string[];
    cascadeFired: boolean;
    // Phase 5.2 — present when `companionFields.due_out` actually
    // changed and the gate produced a canonical audit payload. Caller
    // should attach it to the OCIS event / audit_log row.
    dueOutEditAuditPayload?: DueOutEditAuditPayload;
  };
  error?: string;
}

/**
 * Apply a scheduling mutation to a ticket through the canonical contract.
 *
 * Returns `{ success: true, data }` on success, `{ success: false, error }`
 * on validation or DB failure. Cascade failure does NOT flip success to
 * false — the underlying mutation landed; cascade is best-effort and
 * mirrors `recomputeTicketPredictedMinutes`/`triggerBoardCascade` semantics.
 */
export async function mutateTicketSchedule(
  ticketId: string,
  updates: ScheduleUpdates,
  options: MutateTicketScheduleOptions
): Promise<MutateTicketScheduleResult> {
  const requestedKeys = Object.keys(updates) as ScheduleField[];

  // 1. Allowlist enforcement — fail closed.
  const disallowed = requestedKeys.filter(
    (k) => !ALLOWED_SCHEDULE_FIELDS.includes(k as ScheduleField)
  );
  if (disallowed.length > 0) {
    const msg = `[mutateTicketSchedule] disallowed fields ${JSON.stringify(disallowed)} (reason=${options.reason})`;
    console.error(msg);
    return { success: false, error: msg };
  }

  const companionKeys = Object.keys(options.companionFields ?? {});

  // Empty payload (no schedule fields AND no companion fields) is a no-op
  // success. Match V-A-2 server helper's behavior.
  if (requestedKeys.length === 0 && companionKeys.length === 0) {
    return {
      success: true,
      data: {
        ticketId,
        appliedFields: [],
        appliedCompanionFields: [],
        cascadeFired: false,
      },
    };
  }

  // V-A-5: companion fields MUST NOT shadow scheduling fields. If a caller
  // passes both `predicted_minutes` in updates and in companionFields, the
  // JS spread below would let companionFields silently override the
  // allowlisted value. Fail closed instead. Mirrors V-A-2.
  const collision = companionKeys.filter(
    (k) => ALLOWED_SCHEDULE_FIELDS.includes(k as ScheduleField)
  );
  if (collision.length > 0) {
    const msg = `[mutateTicketSchedule] companion fields collide with schedule allowlist ${JSON.stringify(collision)} — pass them via updates instead (reason=${options.reason})`;
    console.error(msg);
    return { success: false, error: msg };
  }

  // Phase 5.2 — Due Out companion-field gate. If the caller passes
  // `companionFields.due_out` and it differs from
  // `dueOutEditAuditContext.oldDueOut`, the gate validates auth + reason
  // + actor; otherwise it fails closed. Bucket 4.5 §4.5.7: Due Out
  // changes are not normal scheduling mutations.
  const dueOutGate = gateDueOutCompanionField({
    companionFields: options.companionFields ?? null,
    auditContext: options.dueOutEditAuditContext ?? null,
    ticketId,
    now: clock.now(),
  });
  if (!dueOutGate.ok) {
    const msg = `[mutateTicketSchedule] due_out edit rejected: ${dueOutGate.errors.join(', ')} (reason=${options.reason})`;
    console.error(msg);
    return { success: false, error: msg };
  }

  // 2. Build payload. last_edited_* added when caller passes a userId so
  //    we don't have to require a follow-up metadata write.
  const payload: Record<string, unknown> = {
    ...updates,
    ...(options.companionFields ?? {}),
  };
  if (options.userId) {
    payload.last_edited_by_user_id = options.userId;
    payload.last_edited_at = clock.now().toISOString();
  }

  // 3. Fast path for batched callers — skip context fetch when no cascade.
  if (options.skipCascade) {
    const { error: updErr } = await supabase
      .from('tickets')
      .update(payload as never)
      .eq('id', ticketId);

    if (updErr) {
      const msg = `[mutateTicketSchedule] update failed for ${ticketId}: ${updErr.message} (reason=${options.reason})`;
      console.error(msg);
      return { success: false, error: msg };
    }

    return {
      success: true,
      data: {
        ticketId,
        appliedFields: requestedKeys,
        appliedCompanionFields: companionKeys,
        cascadeFired: false,
        dueOutEditAuditPayload: dueOutGate.auditPayload,
      },
    };
  }

  // 4. Fetch ticket context for cascade scoping. We need shop_id always;
  //    scheduled_date and assigned_tech_id are fallbacks if not in updates.
  const { data: ctx, error: ctxErr } = await supabase
    .from('tickets')
    .select('shop_id, scheduled_date, assigned_tech_id, start_time')
    .eq('id', ticketId)
    .maybeSingle();

  if (ctxErr || !ctx) {
    const msg = `[mutateTicketSchedule] failed to load ticket ${ticketId}: ${ctxErr?.message ?? 'not found'} (reason=${options.reason})`;
    console.error(msg);
    return { success: false, error: msg };
  }

  const { error: updErr } = await supabase
    .from('tickets')
    .update(payload as never)
    .eq('id', ticketId);

  if (updErr) {
    const msg = `[mutateTicketSchedule] update failed for ${ticketId}: ${updErr.message} (reason=${options.reason})`;
    console.error(msg);
    return { success: false, error: msg };
  }

  // 5. Resolve cascade scope from post-update intent. Prefer the new value
  //    (scheduled_date/assigned_tech_id from `updates`) so a date or tech
  //    move cascades the new pair rather than the stale one.
  const cascadeDate =
    options.cascadeDateOverride ??
    (typeof updates.scheduled_date === 'string' ? updates.scheduled_date : undefined) ??
    ctx.scheduled_date ??
    (await deriveDateFromStartTimeForShop(ctx.shop_id, ctx.start_time));

  if (!cascadeDate) {
    console.warn(
      `[mutateTicketSchedule] no cascade date resolvable for ${ticketId} — skipping cascade (reason=${options.reason})`
    );
    return {
      success: true,
      data: {
        ticketId,
        appliedFields: requestedKeys,
        appliedCompanionFields: companionKeys,
        cascadeFired: false,
        dueOutEditAuditPayload: dueOutGate.auditPayload,
      },
    };
  }

  let cascadeTech: string | undefined;
  if (options.cascadeTechOverride === null) {
    cascadeTech = undefined;
  } else if (options.cascadeTechOverride !== undefined) {
    cascadeTech = options.cascadeTechOverride;
  } else if (typeof updates.assigned_tech_id === 'string') {
    cascadeTech = updates.assigned_tech_id;
  } else if (Object.prototype.hasOwnProperty.call(updates, 'assigned_tech_id') && updates.assigned_tech_id === null) {
    cascadeTech = undefined; // explicit unassign — fall back to all-tech
  } else {
    cascadeTech = ctx.assigned_tech_id ?? undefined;
  }

  try {
    await triggerBoardCascade(ctx.shop_id, cascadeDate, cascadeTech);
  } catch (e) {
    console.warn(
      `[mutateTicketSchedule] cascade trigger failed for ${ticketId} (reason=${options.reason}):`,
      e
    );
    // Update succeeded; cascade is best-effort. Match recompute pattern.
  }

  return {
    success: true,
    data: {
      ticketId,
      appliedFields: requestedKeys,
      appliedCompanionFields: companionKeys,
      cascadeFired: true,
      dueOutEditAuditPayload: dueOutGate.auditPayload,
    },
  };
}

async function deriveDateFromStartTimeForShop(
  shopId: string,
  startTime: string | null
): Promise<string | undefined> {
  if (!startTime) return undefined;
  try {
    const { data: shop } = await supabase
      .from('shop_profile')
      .select('time_zone')
      .eq('shop_id', shopId)
      .maybeSingle();
    const tz = shop?.time_zone || 'America/Los_Angeles';
    return getShopDayLabel(startTime, tz);
  } catch {
    return undefined;
  }
}

// V-A-12 / V-A-26 — CAS-aware scheduling-mutation primitive.
//
// Compare-and-swap variant of `mutateTicketSchedule`. The caller declares
// `expectedValues` for one or more allowlisted schedule columns, and the
// helper injects predicates into the same single UPDATE query based on
// the value's runtime type:
//   - scalar non-null value → .eq(field, value)
//   - null                  → .is(field, null)   (PostgREST: .eq doesn't match null)
//   - array                 → .in(field, array)  (V-A-26: status-set guards, etc.)
//
// If the row's actual values no longer match the expected values, the
// UPDATE matches zero rows and the helper returns `casMissed: true` —
// caller can then read fresh state and decide whether to retry,
// re-derive, or abort.
//
// Single-UPDATE semantics. No read-then-write. No automatic cascade —
// callers orchestrate cascade themselves so the CAS outcome can drive
// the cascade decision (mirrors the existing pattern at
// `useConvertedRolloverGrowth.ts:127`).
// Minimal builder surface for the CAS-guarded UPDATE below. The loose client
// (schema-agnostic) is assigned into this shallow type cheaply — no deep
// instantiation — and it exposes the two-argument `.select(cols, { count,
// head })` count form the CAS check relies on.
type CasUpdateQuery = {
  is: (col: string, value: unknown) => CasUpdateQuery;
  in: (col: string, values: unknown) => CasUpdateQuery;
  eq: (col: string, value: unknown) => CasUpdateQuery;
  select: (
    cols: string,
    opts: { count: 'exact'; head: true },
  ) => PromiseLike<{ error: { message: string } | null; count: number | null }>;
};

export interface MutateTicketScheduleCASOptions {
  reason: string;
  userId?: string;
  companionFields?: Record<string, unknown>;
  // Required: at least one allowlisted schedule field whose current
  // value the caller expects. The UPDATE will match only rows whose
  // current values still satisfy the expected predicate.
  //   - scalar value → .eq(field, value)
  //   - null         → .is(field, null)
  //   - array        → .in(field, array)  (V-A-26)
  expectedValues: ScheduleUpdates;
}

export interface MutateTicketScheduleCASResult {
  success: boolean;
  data?: {
    ticketId: string;
    appliedFields: ScheduleField[];
    appliedCompanionFields: string[];
    casMissed: boolean;
  };
  error?: string;
}

export async function mutateTicketScheduleCAS(
  ticketId: string,
  updates: ScheduleUpdates,
  options: MutateTicketScheduleCASOptions
): Promise<MutateTicketScheduleCASResult> {
  const requestedKeys = Object.keys(updates) as ScheduleField[];
  const expectedKeys = Object.keys(options.expectedValues ?? {}) as ScheduleField[];
  const companionKeys = Object.keys(options.companionFields ?? {});

  // 1. Allowlist enforcement on the update payload — fail closed.
  const disallowedUpdates = requestedKeys.filter(
    (k) => !ALLOWED_SCHEDULE_FIELDS.includes(k as ScheduleField)
  );
  if (disallowedUpdates.length > 0) {
    const msg = `[mutateTicketScheduleCAS] disallowed update fields ${JSON.stringify(disallowedUpdates)} (reason=${options.reason})`;
    console.error(msg);
    return { success: false, error: msg };
  }

  // 2. Allowlist enforcement on expectedValues — fail closed.
  //    The CAS predicate columns must also be allowlisted; otherwise a caller
  //    could inject `.eq('arbitrary_col', val)` predicates outside the
  //    contract surface.
  const disallowedExpected = expectedKeys.filter(
    (k) => !ALLOWED_SCHEDULE_FIELDS.includes(k as ScheduleField)
  );
  if (disallowedExpected.length > 0) {
    const msg = `[mutateTicketScheduleCAS] disallowed expectedValues fields ${JSON.stringify(disallowedExpected)} (reason=${options.reason})`;
    console.error(msg);
    return { success: false, error: msg };
  }

  // 3. Empty payload (no schedule fields, no companion fields) is a no-op
  //    success — same semantics as the base helper.
  if (requestedKeys.length === 0 && companionKeys.length === 0) {
    return {
      success: true,
      data: {
        ticketId,
        appliedFields: [],
        appliedCompanionFields: [],
        casMissed: false,
      },
    };
  }

  // 4. Companion fields MUST NOT shadow scheduling fields. Fail closed.
  const collision = companionKeys.filter(
    (k) => ALLOWED_SCHEDULE_FIELDS.includes(k as ScheduleField)
  );
  if (collision.length > 0) {
    const msg = `[mutateTicketScheduleCAS] companion fields collide with schedule allowlist ${JSON.stringify(collision)} — pass them via updates instead (reason=${options.reason})`;
    console.error(msg);
    return { success: false, error: msg };
  }

  // Phase 5.3 — Due Out CAS hardening. CAS does not yet carry the
  // Phase 5.2 Due Out audit gate. To prevent ANY future caller from
  // silently slipping a Due Out change through CAS, reject
  // `companionFields.due_out` outright with a machine-readable error.
  // Today no caller exercises this — existing CAS consumers
  // (rolloverChain, ticketTimerManager, useConvertedRolloverGrowth,
  // syncTicketBreakRows) pass non-due-out fields. If a future caller
  // legitimately needs CAS-style Due Out mutation, this guard forces a
  // deliberate adoption of the audit gate rather than allowing a silent
  // path. Bucket 4.5 §4.5.7: Due Out changes are not normal scheduling
  // mutations.
  if (Object.prototype.hasOwnProperty.call(options.companionFields ?? {}, 'due_out')) {
    const msg = `[mutateTicketScheduleCAS] companionFields.due_out is not allowed — CAS does not carry the Phase 5.2 Due Out audit gate. Route Due Out edits through mutateTicketSchedule instead (reason=${options.reason})`;
    console.error(msg);
    return { success: false, error: msg };
  }

  // 5. Build payload identically to the base helper.
  const payload: Record<string, unknown> = {
    ...updates,
    ...(options.companionFields ?? {}),
  };
  if (options.userId) {
    payload.last_edited_by_user_id = options.userId;
    payload.last_edited_at = clock.now().toISOString();
  }

  // 6. Build the single CAS-guarded UPDATE. Predicate dispatch by
  //    runtime type:
  //      - null        → .is(field, null)  (PostgREST: .eq doesn't match null)
  //      - array       → .in(field, array) (V-A-26: status-set guards)
  //      - scalar else → .eq(field, value)
  // The CAS predicate loop reassigns `query` through `.is/.in/.eq`; carrying
  // postgrest's full tickets generics through that chain trips TS2589 near the
  // program-wide instantiation ceiling. Running it through the client widened
  // to its default (schema-agnostic) generic keeps `.from`/`.update` from
  // resolving the tickets relationship graph — zero deep instantiation — while
  // the UPDATE executes identically at runtime.
  const looseClient: SupabaseClient = supabase;
  let query: CasUpdateQuery = looseClient.from('tickets').update(payload).eq('id', ticketId);
  for (const [field, value] of Object.entries(options.expectedValues ?? {})) {
    if (value === null) {
      query = query.is(field, null);
    } else if (Array.isArray(value)) {
      query = query.in(field, value);
    } else {
      query = query.eq(field, value);
    }
  }

  // 7. `.select('id', { count: 'exact', head: true })` returns the count of
  //    matched rows without round-tripping the row body. count===0 means the
  //    CAS predicate failed (or the id no longer exists); callers must
  //    distinguish based on their expectations.
  const { error: updErr, count } = await query.select('id', { count: 'exact', head: true });

  if (updErr) {
    const msg = `[mutateTicketScheduleCAS] update failed for ${ticketId}: ${updErr.message} (reason=${options.reason})`;
    console.error(msg);
    return { success: false, error: msg };
  }

  return {
    success: true,
    data: {
      ticketId,
      appliedFields: requestedKeys,
      appliedCompanionFields: companionKeys,
      casMissed: (count ?? 0) === 0,
    },
  };
}
